/**
 * Safely parses a `contentItems` value from the banner API.
 * Handles both JSON strings and already-parsed objects/arrays.
 *
 * @param {string|Object|Array} raw - The raw contentItems value.
 * @returns {Object|Array|null} The parsed value, or null on failure.
 */
export function parseContentItems(raw) {
    if (!raw) return null;
    try {
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse contentItems:', e);
        return null;
    }
}

/**
 * Builds a tree structure from a list of aisles.
 *
 * @param {Array} aisles - The list of aisles to build the tree from
 * @returns {Array} The tree structure
 */
export function buildAisleTree(aisles = []) {
    const nodeMap = new Map();
    const idSet = new Set();

    // Step 1: Wrap items with children arrays and track IDs
    for (const item of aisles) {
        idSet.add(item.id);
        nodeMap.set(item.id, { ...item, children: [] });
    }

    // Step 2: Attach children to their parents
    for (const item of aisles) {
        const node = nodeMap.get(item.id);
        const parent = nodeMap.get(item.parentID);
        if (parent) {
            parent.children.push(node);
        }
    }

    // Step 3: Sort children arrays by rankNumber
    for (const node of nodeMap.values()) {
        node.children.sort((a, b) => a.rankNumber - b.rankNumber);
    }

    // Step 4: Return top-level nodes (roots)
    return [...nodeMap.values()].filter((node) => !idSet.has(node.parentID));
}

/**
 * Flattens a tree structure into a list of nodes, maintaining the sorted order.
 *
 * @param {Array} tree - The tree to flatten
 * @returns {Array} The flattened list of nodes
 */
export function flattenAisleTree(tree) {
    const result = [];

    function walk(node) {
        result.push(node);
        node.children.forEach(walk);
    }

    tree.forEach(walk);
    return result;
}

/**
 * Groups banners by aisle.
 *
 * @param {Array} banners - The list of banners to group
 * @returns {Object} The grouped banners
 */
export function groupBannersByAisle(banners) {
    if (!Array.isArray(banners)) {
        return {};
    }

    return banners.reduce((acc, banner) => {
        if (!acc[banner.taxonomyID]) {
            acc[banner.taxonomyID] = [];
        }
        acc[banner.taxonomyID].push(banner);
        return acc;
    }, {});
}

/**
 * Groups carousels by aisle.
 *
 * @param {Array} carousels - The list of carousels to group
 * @returns {Object} The grouped carousels
 */
export function groupCarouselsByAisle(carousels) {
    if (!Array.isArray(carousels)) {
        return {};
    }

    const grouped = carousels.reduce((acc, carousel) => {
        const targetID = carousel.l2AisleID || carousel.l1AisleID;

        if (!acc[targetID]) {
            acc[targetID] = [];
        }

        acc[targetID].push(carousel);
        return acc;
    }, {});

    // Sort each group by rank in descending order (higher ranks first)
    Object.keys(grouped).forEach((key) => {
        grouped[key].sort((a, b) => (b.rankNumber || 0) - (a.rankNumber || 0));
    });

    return grouped;
}

/**
 * Filters the aisle tree based on search term, searching by name and ID.
 * Preserves tree structure by keeping parent nodes if children match.
 *
 * @param {Array} tree - The aisle tree to filter
 * @param {string} searchTerm - The search term to filter by
 * @returns {Array} The filtered tree
 */
export function filterAisleTree(tree, searchTerm) {
    if (!searchTerm.trim()) {
        return tree;
    }

    const search = searchTerm.toLowerCase();

    function filterNode(node) {
        const nameMatches = node.name.toLowerCase().includes(search);
        const idMatches = node.id.toString().includes(search);
        const nodeMatches = nameMatches || idMatches;

        // Recursively filter children
        const filteredChildren = node.children.map(filterNode).filter(Boolean);

        // Include node if it matches or has matching children
        if (nodeMatches || filteredChildren.length > 0) {
            return {
                ...node,
                children: filteredChildren,
            };
        }

        return null;
    }

    return tree.map(filterNode).filter(Boolean);
}

/**
 * Downloads a CSV containing carousel data.
 *
 * @param {Array} aisles - The list of aisles
 * @param {Array} carousels - The list of carousels
 * @param {number} packGroupID - The ID of the pack group
 */
export function downloadCarouselCSV(aisles, carousels, packGroupID) {
    const rows = [];
    const headers = ['Carousel ID', 'Carousel Name', 'Carousel Type', 'Aisle ID', 'Aisle Name'];

    rows.push(headers);

    carousels.forEach((carousel) => {
        if (carousel.id) {
            const carouselAisle = aisles.find(
                (aisle) => aisle.id === carousel.l1AisleID || aisle.id === carousel.l2AisleID,
            );
            if (carouselAisle) {
                const row = [carousel.id, carousel.name, carousel.displayType, carouselAisle.id, carouselAisle.name];
                rows.push(row);
            }
        }
    });

    const csvContent = rows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });

    const downloadLink = document.createElement('a');
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = `Pack Group ${packGroupID} - Carousels.csv`;
    downloadLink.click();
    URL.revokeObjectURL(downloadLink.href);
}
